import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Simple second-granularity countdown timer.
 *
 * `start(seconds)` (re)starts the countdown from the given value; `count` ticks
 * down to 0 once per second. If an `initialValue > 0` is passed, the countdown
 * starts automatically on mount. The interval is always cleared on unmount.
 */
export function useCountdown(initialValue = 0) {
  const [count, setCount] = useState(initialValue);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initialValueRef = useRef(initialValue);

  const start = useCallback((seconds: number) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setCount(seconds);
    if (seconds <= 0) {
      return;
    }
    intervalRef.current = setInterval(() => {
      setCount((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(intervalRef.current!);
        }
        return next;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    if (initialValueRef.current > 0) {
      start(initialValueRef.current);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [start]);

  return { count, start };
}
